import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type {
  BoggleCompletedRun,
  BoggleWordModeCounts,
  BoggleWordOccurrence,
  BoggleWordProjectionSnapshot,
  BoggleWordStat,
} from './types'
import { BOGGLE_WORD_METADATA_VERSION } from './types'
import {
  deriveBoggleWordLexicalTags,
  normalizeBoggleSubmittedWord,
  type BoggleWordMetadataLexicon,
} from './wordMetadata'

const RATING_RANK = new Map<BoggleRunSummaryRatingId, number>(
  BOGGLE_RUN_SUMMARY_RATINGS.map((rating, index) => [rating.id, index]),
)

function getHigherRatingId(
  current: BoggleRunSummaryRatingId,
  candidate: BoggleRunSummaryRatingId,
) {
  return (RATING_RANK.get(candidate) ?? -1) > (RATING_RANK.get(current) ?? -1)
    ? candidate
    : current
}

function compareIsoThenId(a: BoggleCompletedRun, b: BoggleCompletedRun) {
  const completedAtOrder = a.completedAt.localeCompare(b.completedAt)
  return completedAtOrder !== 0 ? completedAtOrder : a.id.localeCompare(b.id)
}

function resolveWordOccurrencePlayedAt(
  run: BoggleCompletedRun,
  submittedWordIndex: number,
) {
  const acceptedFact = run.acceptedWordTimeline?.[submittedWordIndex]
  if (acceptedFact) {
    return new Date(
      new Date(run.startedAt).getTime() + acceptedFact.acceptedAtMs,
    ).toISOString()
  }
  return run.completedAt
}

function incrementModeCount(
  counts: BoggleWordModeCounts,
  occurrence: Pick<BoggleWordOccurrence, 'gameModeId' | 'runKind'>,
): BoggleWordModeCounts {
  const byRunKind = counts[occurrence.gameModeId] ?? {}
  return {
    ...counts,
    [occurrence.gameModeId]: {
      ...byRunKind,
      [occurrence.runKind]: (byRunKind[occurrence.runKind] ?? 0) + 1,
    },
  }
}

export function buildBoggleWordProjections(
  completedRuns: readonly BoggleCompletedRun[],
  metadataLexicon: BoggleWordMetadataLexicon,
): BoggleWordProjectionSnapshot {
  const wordOccurrences: BoggleWordOccurrence[] = []

  for (const run of [...completedRuns].sort(compareIsoThenId)) {
    const acceptedWords = run.acceptedWordTimeline?.map(word => word.word)
    const submittedWords =
      acceptedWords && acceptedWords.length > 0 ? acceptedWords : run.submittedWords
    submittedWords.forEach((displayWord, submittedWordIndex) => {
      const normalizedWord = normalizeBoggleSubmittedWord(displayWord)
      if (!normalizedWord) {
        return
      }
      wordOccurrences.push({
        displayWord,
        gameModeId: run.gameModeId,
        id: `${run.id}:${submittedWordIndex}`,
        lexiconVersion: run.lexiconVersion,
        normalizedWord,
        playedAt: resolveWordOccurrencePlayedAt(run, submittedWordIndex),
        ratingId: run.ratingId,
        runId: run.id,
        runKind: run.runKind,
        submittedWordIndex,
        wordMetadataVersion: run.wordMetadataVersion,
      })
    })
  }

  const statsByWord = new Map<string, BoggleWordStat>()
  for (const occurrence of wordOccurrences) {
    const previous = statsByWord.get(occurrence.normalizedWord)
    if (!previous) {
      statsByWord.set(occurrence.normalizedWord, {
        displayWord: occurrence.displayWord,
        firstPlayedAt: occurrence.playedAt,
        highestRatingId: occurrence.ratingId,
        lastPlayedAt: occurrence.playedAt,
        length: occurrence.normalizedWord.length,
        lexicalTags: deriveBoggleWordLexicalTags(
          occurrence.normalizedWord,
          metadataLexicon,
        ),
        lexiconVersion: metadataLexicon.lexiconVersion,
        normalizedWord: occurrence.normalizedWord,
        perModeCounts: incrementModeCount({}, occurrence),
        timesPlayed: 1,
        wordMetadataVersion: BOGGLE_WORD_METADATA_VERSION,
      })
      continue
    }

    statsByWord.set(occurrence.normalizedWord, {
      ...previous,
      highestRatingId: getHigherRatingId(previous.highestRatingId, occurrence.ratingId),
      lastPlayedAt:
        occurrence.playedAt > previous.lastPlayedAt
          ? occurrence.playedAt
          : previous.lastPlayedAt,
      perModeCounts: incrementModeCount(previous.perModeCounts, occurrence),
      timesPlayed: previous.timesPlayed + 1,
    })
  }

  return {
    wordOccurrences,
    wordStats: [...statsByWord.values()].sort((a, b) =>
      a.normalizedWord.localeCompare(b.normalizedWord),
    ),
  }
}
